import { FC, useEffect, useState, useCallback, useRef } from 'react'
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import axios from 'lib/axios'
import config from 'config'
import theme from 'theme'
import { useAppSelector, useAppDispatch } from 'store/hooks'
import { nexaAIActivatingSelector, setNexaAIStatus } from 'store/user/userSlice'
import { UserServiceV4 } from 'services'

import { useStreamingChat } from './hooks/useStreamingChat'
import { useThreads } from './hooks/useThreads'
import NexaSidebar from './components/nexa-sidebar/NexaSidebar'
import { ChatArea } from './components/chat-area'
import { DeleteChatModal } from './components/delete-chat-modal'
import { RenameChatModal } from './components/rename-chat-modal'
import { SidebarIcon } from './components/icons'
import { ActivationPending } from './components/activation-pending'
import {
  NexaAIContainer,
  MainContent,
  Header,
  HeaderLeft,
  HeaderTitle,
  BetaTag,
  ThreadInfoBar,
  ThreadTitle,
  ThreadMenuButton
} from './nexaAI.styles'
import { ChatMessage, SnowflakeThreadMessage, ToolCall } from './utils/types'
import { AGENT_READY_STATES } from 'models'
import { ANIMATION_DURATION } from './components/nexa-sidebar/nexaSidebar.styles'

const RenameIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6667 1.44775C12.9143 1.44775 13.1595 1.49653 13.3883 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.6667L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z"
      stroke="#D0C3CC"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 4H3.33333H14" stroke="#E74C3C" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M5.33334 4.00004V2.66671C5.33334 2.31309 5.47382 1.97395 5.72387 1.7239C5.97392 1.47385 6.31305 1.33337 6.66668 1.33337H9.33334C9.68697 1.33337 10.0261 1.47385 10.2761 1.7239C10.5262 1.97395 10.6667 2.31309 10.6667 2.66671V4.00004M12.6667 4.00004V13.3334C12.6667 13.687 12.5262 14.0261 12.2761 14.2762C12.0261 14.5262 11.687 14.6667 11.3333 14.6667H4.66668C4.31305 14.6667 3.97392 14.5262 3.72387 14.2762C3.47382 14.0261 3.33334 13.687 3.33334 13.3334V4.00004H12.6667Z"
      stroke="#E74C3C"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const POLL_INTERVAL = 10000

const NexaAI: FC = () => {
  const dispatch = useAppDispatch()
  const nexaAIActivating = useAppSelector(nexaAIActivatingSelector)
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [showHeaderContent, setShowHeaderContent] = useState(false)
  const [agentConfig, setAgentConfig] = useState<{
    agentRunEndpoint: string
    token: string
  } | null>(null)
  const [userId, setUserId] = useState<string | null>(null)
  const [isLoadingThread, setIsLoadingThread] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMoreMessages, setHasMoreMessages] = useState(false)
  const [currentOffset, setCurrentOffset] = useState(0)
  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean
    threadId: string
    threadName: string
  }>({ isOpen: false, threadId: '', threadName: '' })
  const [renameModalState, setRenameModalState] = useState<{
    isOpen: boolean
    threadId: string
    threadName: string
  }>({ isOpen: false, threadId: '', threadName: '' })
  const [threadMenuAnchor, setThreadMenuAnchor] = useState<HTMLElement | null>(null)

  const loadingThreadIdRef = useRef<string | null>(null)

  // Delay showing header content until sidebar animation completes
  useEffect(() => {
    if (!isSidebarOpen) {
      const timer = setTimeout(() => {
        setShowHeaderContent(true)
      }, ANIMATION_DURATION)
      return () => clearTimeout(timer)
    } else {
      // Hide header content immediately when opening sidebar
      setShowHeaderContent(false)
    }
  }, [isSidebarOpen])

  const {
    threads,
    activeThreadId,
    isLoading: isLoadingThreads,
    loadThreads,
    createThreadSilent,
    loadThread,
    deleteThread,
    renameThread,
    setActiveThreadId
  } = useThreads({
    // Snowflake limits origin_application to 16 bytes, so truncate the user ID
    originApplication: userId ? userId.replace(/-/g, '').substring(0, 16) : undefined
  })

  const threadMessagesCache = useRef<Map<string, ChatMessage[]>>(new Map())

  const handleMessageDone = useCallback(() => {
    // Don't refresh threads here - for new threads, we wait for title generation
    // to avoid showing "Untitled" in the sidebar. The thread list will be
    // refreshed by handleTitleGenerated after the title is set.
  }, [])

  const handleTitleGenerated = useCallback(() => {
    loadThreads().catch(() => {
      // Ignore errors if component unmounted
    })
  }, [loadThreads])

  const {
    messages,
    agentStatus,
    isLoading,
    error,
    sendMessage,
    stop,
    newThread,
    setMessages,
    setThreadId,
    setParentMessageId
  } = useStreamingChat({
    accountUrl: config.apiHost,
    endpoint: agentConfig?.agentRunEndpoint || '',
    threadId: activeThreadId || null,
    createThread: async () => {
      // Use createThreadSilent so the thread doesn't appear in sidebar until title is generated
      const newThreadId = await createThreadSilent()
      setActiveThreadId(newThreadId)
      return newThreadId
    },
    onError: (err) => {
      console.error('Chat error:', err)
    },
    onDone: handleMessageDone,
    // Title generation is handled by streamingManager for new threads
    titleGeneration:
      agentConfig && userId
        ? {
            agentRunEndpoint: agentConfig.agentRunEndpoint,
            userId,
            onTitleGenerated: handleTitleGenerated
          }
        : undefined
  })

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await UserServiceV4.getMe()
        setUserId(user.id)
      } catch (err) {
        console.error('Error fetching user:', err)
      }
    }
    fetchUser()
  }, [])

  useEffect(() => {
    let pollingInterval: ReturnType<typeof setInterval> | null = null
    let cancelled = false

    const checkAgentReady = async (): Promise<boolean> => {
      try {
        const initResponse = await axios.get('/api/v4/agents/initialize')
        if (initResponse.data?.initialized) {
          const agentsResponse = await axios.get('/api/v4/agents')
          const agents = agentsResponse.data?.agents || []
          if (agents.length > 0) {
            const agent = agents[0]
            if (AGENT_READY_STATES.includes(agent.state_type)) {
              const agentRunEndpoint = `/api/v4/cortex/agents/${agent.name}/run`
              if (!cancelled) {
                setAgentConfig({ agentRunEndpoint, token: '' })
                dispatch(setNexaAIStatus({ enabled: true, agentName: agent.name }))
              }
              return true
            }
          }
        }
      } catch (err) {
        console.error('Agent initialize error:', err)
      }
      return false
    }

    const initializeAgent = async () => {
      const isReady = await checkAgentReady()
      if (!isReady && nexaAIActivating && !cancelled) {
        // Agent not ready yet — poll until it is
        pollingInterval = setInterval(async () => {
          const ready = await checkAgentReady()
          if (ready && pollingInterval) {
            clearInterval(pollingInterval)
            pollingInterval = null
          }
        }, POLL_INTERVAL)
      }
    }

    initializeAgent()

    return () => {
      cancelled = true
      if (pollingInterval) {
        clearInterval(pollingInterval)
      }
    }
  }, [nexaAIActivating, dispatch])

  useEffect(() => {
    if (agentConfig && userId) {
      loadThreads()
    }
  }, [agentConfig, userId, loadThreads])

  useEffect(() => {
    setThreadId(activeThreadId)
  }, [activeThreadId, setThreadId])

  useEffect(() => {
    if (activeThreadId && messages.length > 0) {
      threadMessagesCache.current.set(activeThreadId, [...messages])
    }
  }, [activeThreadId, messages])

  const convertToMessages = useCallback((snowflakeMessages: SnowflakeThreadMessage[]): ChatMessage[] => {
    // Sort messages by created_on ascending (oldest first) for correct chat order
    const sortedMessages = [...snowflakeMessages].sort((a, b) => a.created_on - b.created_on)

    return sortedMessages.map((msg) => {
      // Parse message_payload if it's a string (API returns JSON string)
      let payload = msg.message_payload
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload)
        } catch (e) {
          console.error('Failed to parse message_payload:', e)
          payload = { content: [] }
        }
      }

      // Extract text content from the payload
      // Structure: { content: [{ type: "text", text: "..." }, { type: "thinking", ... }, { type: "table", table: {...} }] }
      const contentArray = payload.content || payload || []
      const textContent = Array.isArray(contentArray)
        ? contentArray
            .filter((item: { type?: string }) => item.type === 'text')
            .map((item: { text?: string }) => item.text || '')
            .join('')
        : ''

      // Extract table data from content blocks
      // Structure: { type: "table", table: { result_set: {...}, title: "...", tool_use_id: "..." } }
      const toolCalls: ToolCall[] = []
      if (Array.isArray(contentArray)) {
        contentArray
          .filter((item: { type?: string }) => item.type === 'table')
          .forEach((item: { table?: { result_set?: unknown; title?: string; tool_use_id?: string } }) => {
            if (item.table?.result_set) {
              toolCalls.push({
                id: item.table.tool_use_id || `table-${Date.now()}`,
                name: 'table',
                input: {},
                resultSet: item.table.result_set as ToolCall['resultSet'],
                resultTitle: item.table.title
              })
            }
          })
      }

      // Extract chart data from content blocks
      // Structure: { type: "chart", chart: { chart_spec: "...", tool_use_id: "..." } }
      const charts: string[] = []
      if (Array.isArray(contentArray)) {
        contentArray
          .filter((item: { type?: string }) => item.type === 'chart')
          .forEach((item: { chart?: { chart_spec?: string; tool_use_id?: string } }) => {
            if (item.chart?.chart_spec) {
              // Try to add to existing tool call if tool_use_id matches
              const existingToolCall = toolCalls.find((tc) => tc.id === item.chart?.tool_use_id)
              if (existingToolCall) {
                existingToolCall.chartSpec = item.chart.chart_spec
              } else {
                // Otherwise add to standalone charts array
                charts.push(item.chart.chart_spec)
              }
            }
          })
      }

      return {
        id: String(msg.message_id),
        role: msg.role,
        content: textContent,
        toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
        charts: charts.length > 0 ? charts : undefined,
        timestamp: new Date(msg.created_on),
        status: 'complete' as const
      }
    })
  }, [])

  const handleSelectThread = useCallback(
    async (threadId: string) => {
      // Don't reload if already on this thread
      if (threadId === activeThreadId) return

      // Save current messages to cache before switching
      if (activeThreadId && messages.length > 0) {
        threadMessagesCache.current.set(activeThreadId, [...messages])
      }

      // Set loading state and track which thread we're loading
      setIsLoadingThread(true)
      loadingThreadIdRef.current = threadId

      // Reset pagination state
      setCurrentOffset(0)
      setHasMoreMessages(false)

      // Clear current messages immediately to prevent confusion
      setMessages([])

      // Set the active thread ID immediately for UI feedback
      setActiveThreadId(threadId)

      try {
        // Load thread data from Snowflake
        const threadData = await loadThread(threadId)

        // Check if user switched to a different thread while we were loading
        if (loadingThreadIdRef.current !== threadId) {
          return
        }

        const threadMessages = threadData.messages || []
        const chatMessages = convertToMessages(threadMessages)
        setMessages(chatMessages)

        // Update pagination state
        setCurrentOffset(threadMessages.length)
        setHasMoreMessages(threadData.has_more || false)

        // Set parent_message_id to the most recent message in the thread
        // API returns messages in descending order (newest first), so index 0 is the most recent
        if (threadMessages.length > 0) {
          const mostRecentMessage = threadMessages[0]
          setParentMessageId(String(mostRecentMessage.message_id))
        } else {
          // Empty thread - use 0 for first message per spec
          setParentMessageId('0')
        }
      } catch (err) {
        console.error('Error loading thread:', err)
        // Only show error if this is still the thread we're trying to load
        if (loadingThreadIdRef.current === threadId) {
          setMessages([])
        }
      } finally {
        // Only clear loading state if this is still the thread we're loading
        if (loadingThreadIdRef.current === threadId) {
          setIsLoadingThread(false)
          loadingThreadIdRef.current = null
        }
      }
    },
    [loadThread, convertToMessages, setMessages, setParentMessageId, activeThreadId, messages, setActiveThreadId]
  )

  const handleLoadMore = useCallback(async () => {
    if (!activeThreadId || isLoadingMore || !hasMoreMessages) return

    setIsLoadingMore(true)

    try {
      const threadData = await loadThread(activeThreadId, { offset: currentOffset })

      const olderMessages = threadData.messages || []
      const olderChatMessages = convertToMessages(olderMessages)

      // Prepend older messages to existing messages
      setMessages([...olderChatMessages, ...messages])

      // Update pagination state
      setCurrentOffset(currentOffset + olderMessages.length)
      setHasMoreMessages(threadData.has_more || false)
    } catch (err) {
      console.error('Error loading more messages:', err)
    } finally {
      setIsLoadingMore(false)
    }
  }, [
    activeThreadId,
    isLoadingMore,
    hasMoreMessages,
    currentOffset,
    loadThread,
    convertToMessages,
    setMessages,
    messages
  ])

  const handleNewChat = useCallback(() => {
    // Save current messages to cache before starting new chat
    if (activeThreadId && messages.length > 0) {
      threadMessagesCache.current.set(activeThreadId, [...messages])
    }
    // Cancel any pending thread load
    loadingThreadIdRef.current = null
    setIsLoadingThread(false)

    // Reset pagination state
    setCurrentOffset(0)
    setHasMoreMessages(false)

    // Clear state - thread will be created when user sends first message
    newThread()
    setActiveThreadId(null)
    // Initial parent_message_id should be 0 per spec
    setParentMessageId('0')
  }, [activeThreadId, messages, setParentMessageId, newThread, setActiveThreadId])

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev)
  }, [])

  const handleOpenDeleteModal = useCallback((threadId: string, threadName: string) => {
    setDeleteModalState({ isOpen: true, threadId, threadName })
  }, [])

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalState({ isOpen: false, threadId: '', threadName: '' })
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    try {
      await deleteThread(deleteModalState.threadId)
      // If we deleted the active thread, clear messages
      if (deleteModalState.threadId === activeThreadId) {
        newThread()
      }
    } catch (err) {
      console.error('Error deleting thread:', err)
    } finally {
      handleCloseDeleteModal()
    }
  }, [deleteThread, deleteModalState.threadId, activeThreadId, newThread, handleCloseDeleteModal])

  const handleOpenRenameModal = useCallback((threadId: string, threadName: string) => {
    setRenameModalState({ isOpen: true, threadId, threadName })
  }, [])

  const handleCloseRenameModal = useCallback(() => {
    setRenameModalState({ isOpen: false, threadId: '', threadName: '' })
  }, [])

  const handleConfirmRename = useCallback(
    async (newName: string) => {
      try {
        await renameThread(renameModalState.threadId, newName)
      } catch (err) {
        console.error('Error renaming thread:', err)
      } finally {
        handleCloseRenameModal()
      }
    },
    [renameThread, renameModalState.threadId, handleCloseRenameModal]
  )

  const handleSendMessage = useCallback(
    (message: string) => {
      if (agentConfig) {
        sendMessage(message)
      }
    },
    [agentConfig, sendMessage]
  )

  // Thread info bar menu handlers
  const handleThreadMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setThreadMenuAnchor(event.currentTarget)
  }, [])

  const handleThreadMenuClose = useCallback(() => {
    setThreadMenuAnchor(null)
  }, [])

  const handleThreadMenuRename = useCallback(() => {
    if (activeThreadId) {
      const thread = threads.find((t) => t.thread_id === activeThreadId)
      const threadName = thread?.thread_name || 'Untitled'
      handleOpenRenameModal(activeThreadId, threadName)
    }
    handleThreadMenuClose()
  }, [activeThreadId, threads, handleOpenRenameModal, handleThreadMenuClose])

  const handleThreadMenuDelete = useCallback(() => {
    if (activeThreadId) {
      const thread = threads.find((t) => t.thread_id === activeThreadId)
      const threadName = thread?.thread_name || 'Untitled'
      handleOpenDeleteModal(activeThreadId, threadName)
    }
    handleThreadMenuClose()
  }, [activeThreadId, threads, handleOpenDeleteModal, handleThreadMenuClose])

  // Get current thread name
  const currentThreadName = activeThreadId
    ? threads.find((t) => t.thread_id === activeThreadId)?.thread_name || 'Untitled'
    : ''

  // Show thread info bar when sidebar is closed and there are messages
  const showThreadInfoBar = showHeaderContent && messages.length > 0

  // Show activation pending screen if agent is activating and not ready yet
  if (nexaAIActivating && !agentConfig) {
    return (
      <NexaAIContainer>
        <MainContent>
          <Header>
            <HeaderLeft>
              <HeaderTitle>Nexa AI Agent</HeaderTitle>
              <BetaTag>Beta</BetaTag>
            </HeaderLeft>
          </Header>
          <ActivationPending />
        </MainContent>
      </NexaAIContainer>
    )
  }
  return (
    <NexaAIContainer>
      {/* Left Sidebar */}
      <NexaSidebar
        threads={threads}
        activeThreadId={activeThreadId}
        isLoadingThreads={isLoadingThreads}
        onNewChat={handleNewChat}
        onSelectThread={handleSelectThread}
        onOpenDeleteModal={handleOpenDeleteModal}
        onOpenRenameModal={handleOpenRenameModal}
        onToggleSidebar={handleToggleSidebar}
        isCollapsed={!isSidebarOpen}
      />

      {/* Delete Chat Modal */}
      <DeleteChatModal
        isOpen={deleteModalState.isOpen}
        chatName={deleteModalState.threadName}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />

      {/* Rename Chat Modal */}
      <RenameChatModal
        isOpen={renameModalState.isOpen}
        currentName={renameModalState.threadName}
        onClose={handleCloseRenameModal}
        onSave={handleConfirmRename}
      />

      {/* Main Content */}
      <MainContent>
        {/* Header */}
        <Header>
          <HeaderLeft>
            {showHeaderContent && (
              <>
                <HeaderTitle>Nexa AI Agent</HeaderTitle>
                <BetaTag>Beta</BetaTag>
                <IconButton onClick={handleToggleSidebar} sx={{ color: 'inherit' }}>
                  <SidebarIcon />
                </IconButton>
              </>
            )}
          </HeaderLeft>
        </Header>

        {/* Thread Info Bar - shown when sidebar is closed and there are messages */}
        {showThreadInfoBar && (
          <ThreadInfoBar>
            <ThreadTitle>{currentThreadName}</ThreadTitle>
            <ThreadMenuButton onClick={handleThreadMenuOpen}>
              <MoreVertIcon sx={{ color: '#d0c3cc', fontSize: 24 }} />
            </ThreadMenuButton>
            <Menu
              anchorEl={threadMenuAnchor}
              open={Boolean(threadMenuAnchor)}
              onClose={handleThreadMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  backgroundColor: '#372e35',
                  borderRadius: '12px',
                  boxShadow: '0px 4px 8px 0px rgba(30, 26, 29, 0.2)',
                  minWidth: '120px',
                  marginTop: '12px',
                  '& .MuiList-root': {
                    padding: 0
                  }
                }
              }}
            >
              <MenuItem
                onClick={handleThreadMenuRename}
                sx={{
                  padding: '16px 16px 10px 16px',
                  borderBottom: '1px solid #4e444b',
                  '&:hover': {
                    backgroundColor: '#4e444b'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: '24px' }}>
                  <RenameIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Rename"
                  primaryTypographyProps={{
                    fontFamily: theme.typography.fontFamily.primary,
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#e9e0e4',
                    lineHeight: '22px'
                  }}
                />
              </MenuItem>
              <MenuItem
                onClick={handleThreadMenuDelete}
                sx={{
                  padding: '10px 16px 16px 16px',
                  '&:hover': {
                    backgroundColor: '#4e444b'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: '24px' }}>
                  <DeleteIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Delete"
                  primaryTypographyProps={{
                    fontFamily: theme.typography.fontFamily.primary,
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#e74c3c',
                    lineHeight: '22px'
                  }}
                />
              </MenuItem>
            </Menu>
          </ThreadInfoBar>
        )}

        {/* Chat Area */}
        <ChatArea
          messages={messages}
          agentStatus={agentStatus}
          isLoading={isLoading}
          isLoadingThread={isLoadingThread}
          isLoadingMore={isLoadingMore}
          hasMoreMessages={hasMoreMessages}
          isAgentReady={!!agentConfig}
          error={error}
          onSendMessage={handleSendMessage}
          onStop={stop}
          onLoadMore={handleLoadMore}
        />
      </MainContent>
    </NexaAIContainer>
  )
}

export default NexaAI
